import React, { useRef, useState } from "react";
import styles from "../../styles/Select.module.css";

interface MoneyInputProps {
  label: string;
  value: string | number | undefined;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
  labelStyle?: React.CSSProperties;
  error?: string;
}

const parseValue = (value: string | number | undefined) => {
  const str = value !== undefined && value !== null ? String(value) : "0.00";
  const [integer, decimal] = str.split(".");
  return {
    integer: integer || "0",
    cents: (decimal || "").padEnd(2, "0").slice(0, 2),
  };
};

const formatInteger = (val: string) =>
  val.replace(/\B(?=(\d{3})+(?!\d))/g, " ");

export const MoneyInput: React.FC<MoneyInputProps> = ({
  label,
  value,
  onChange,
  disabled,
  style,
  inputStyle,
  labelStyle,
  error,
}) => {
  const [focused, setFocused] = useState(false);

  const { integer: rawInteger, cents } = parseValue(value);
  const displayInteger = rawInteger === "0" && focused ? "" : rawInteger;

  const integerInputRef = useRef<HTMLInputElement>(null);
  const centsInputRef = useRef<HTMLInputElement>(null);

  const handleIntegerChange = (val: string) => {
    const clean = val.replace(/\D/g, "");
    if (clean.length > 12) return;
    onChange(`${clean || "0"}.${cents}`);
  };

  const handleIntegerKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "," || e.key === "." || e.key === "Decimal" || e.key === "Tab") {
      e.preventDefault();
      centsInputRef.current?.focus();
      setTimeout(() => centsInputRef.current?.setSelectionRange(0, 0), 0);
      return;
    }
    if (e.key === "ArrowRight") {
      const target = e.currentTarget;
      if (target.selectionStart === target.value.length) {
        e.preventDefault();
        centsInputRef.current?.focus();
        setTimeout(() => centsInputRef.current?.setSelectionRange(0, 0), 0);
      }
    }
  };

  const handleCentsKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const target = e.currentTarget;
    const selectionStart = target.selectionStart ?? 0;

    if (/^\d$/.test(e.key)) {
      e.preventDefault();

      let newCents = cents;
      if (cents === "00") {
        newCents = e.key + "0";
        onChange(`${rawInteger}.${newCents}`);
        setTimeout(() => centsInputRef.current?.setSelectionRange(1, 1), 0);
      } else if (selectionStart === 0) {
        newCents = e.key + cents[1];
        onChange(`${rawInteger}.${newCents}`);
        setTimeout(() => centsInputRef.current?.setSelectionRange(1, 1), 0);
      } else if (selectionStart === 1) {
        newCents = cents[0] + e.key;
        onChange(`${rawInteger}.${newCents}`);
        setTimeout(() => centsInputRef.current?.setSelectionRange(2, 2), 0);
      }
      return;
    }

    if (e.key === "Backspace") {
      e.preventDefault();

      if (cents === "00") {
        integerInputRef.current?.focus();
        return;
      }

      if (selectionStart === 2) {
        const newCents = cents[0] + "0";
        onChange(`${rawInteger}.${newCents}`);
        setTimeout(() => centsInputRef.current?.setSelectionRange(1, 1), 0);
      } else if (selectionStart === 1 || selectionStart === 0) {
        onChange(`${rawInteger}.00`);
        integerInputRef.current?.focus();
        setTimeout(() => {
          const len = integerInputRef.current?.value.length ?? 0;
          integerInputRef.current?.setSelectionRange(len, len);
        }, 0);
      }
      return;
    }

    if (e.key === "ArrowLeft" && selectionStart === 0) {
      e.preventDefault();
      integerInputRef.current?.focus();
      setTimeout(() => {
        const len = integerInputRef.current?.value.length ?? 0;
        integerInputRef.current?.setSelectionRange(len, len);
      }, 0);
    }
  };


  const borderColor = error
    ? "var(--vivid-pink)"
    : focused
      ? "var(--blue)"
      : "var(--disabled-c)";

  return (
    <div className={styles.formControl} style={style}>
      <div
        className={styles.selectWrapper}
        style={{
          display: "flex",
          alignItems: "center",
          border: `1px solid ${borderColor}`,
          borderRadius: "10px",
          height: "48px",
          backgroundColor: disabled ? "var(--disabled-bg)" : "var(--white)",
          padding: "0 45px 0 14px",
          cursor: disabled ? "not-allowed" : "text",
          transition: "border-color 200ms",
          position: "relative",
          ...inputStyle,
        }}
        onClick={() => !disabled && integerInputRef.current?.focus()}
      >
        <div style={{ display: "flex", alignItems: "center", width: "fit-content" }}>
          <input
            ref={integerInputRef}
            type="text"
            value={formatInteger(displayInteger)}
            disabled={disabled}
            placeholder={focused ? "0" : ""}
            onChange={(e) => handleIntegerChange(e.target.value)}
            onKeyDown={handleIntegerKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            style={{
              border: "none",
              outline: "none",
              padding: 0,
              width: `${Math.max(formatInteger(displayInteger).length, 1)}ch`,
              minWidth: "1ch",
              textAlign: "left",
              fontFamily: "inherit",
              fontSize: "14px",
              background: "transparent",
              color: disabled ? "var(--disabled-c)" : "var(--black)",
            }}
          />

          <span style={{
            fontSize: "14px",
            color: disabled ? "var(--disabled-c)" : "var(--black)",
            padding: "0 1px",
            flexShrink: 0,
          }}>
            ,
          </span>

          <input
            ref={centsInputRef}
            type="text"
            value={cents}
            disabled={disabled}
            onChange={() => {""
            }}
            onKeyDown={handleCentsKeyDown}
            onFocus={() => {
              setFocused(true);
              setTimeout(() => centsInputRef.current?.setSelectionRange(0, 0), 0);
            }}
            onBlur={() => setFocused(false)}
            style={{
              border: "none",
              outline: "none",
              padding: 0,
              width: "22px",
              flexShrink: 0,
              fontFamily: "inherit",
              fontSize: "14px",
              background: "transparent",
              color: disabled ? "var(--disabled-c)" : "var(--black)",
            }}
          />
        </div>

        <span style={{
          position: "absolute",
          right: "12px",
          top: "50%",
          transform: "translateY(-50%)",
          fontSize: "11px",
          fontWeight: 700,
          color: focused ? "var(--blue)" : "var(--disabled-c)",
          pointerEvents: "none",
        }}>
          грн
        </span>

        <label style={{
          position: "absolute",
          left: "10px",
          top: "-8px", 
          padding: "0 6px",
          fontSize: "14px",
          color: error ? "var(--vivid-pink)" : focused ? "var(--blue)" : "var(--black)",
          background: "white",
          pointerEvents: "none",
          zIndex: 1,
          fontWeight: 700,
          transition: "all 0.2s ease",
          whiteSpace: "nowrap",
          ...labelStyle,
        }}>
          {label}
        </label>
      </div>

      {error && (
        <div style={{
          color: "var(--vivid-pink)",
          fontSize: "11px",
          paddingLeft: "4px",
          marginTop: "2px",
        }}>
          {error}
        </div>
      )}
    </div>
  );
};